import { DealPipelineInvestmentsQuery, DealPipelineInvestmentWorkflowStateFiltersQuery, ListInvestmentSortBy } from '@/__generated__/graphql';
import { EnvelopeIcon } from '@/assets/icons/envelope';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { SORT_ORDER, TableColumns } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { TableColumnsEditor } from '@/shared/ui/table';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { SECURITY_STATUS } from '@trace-8/trace8-schema';
import { debounce, partition } from 'lodash';
import { useEffect, useState } from 'react';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../basicComponents/button';
import { IFilterBarData } from '../basicComponents/filterBar';
import { ISelectValue } from '../basicComponents/select';
import { createSimpleSelectOption } from '../basicComponents/select/utils';
import Header from '../header';
import { Loader } from '../loaders/loader';
import { LoaderOnWholeScreen } from '../loaders/loader-on-whole-screen';
import MobileModal from '../mobileModal';
import { GET_ASSET_CLASSES_QUERY } from '../settings-page/assetClassDefaultTargets/queries';
import { initialValuesFromTable } from '../table/constants';
import FilterPage from '../table/filterPage';
import { dealPipelineMobileSort, dealPipelineTableColumns } from './constants';
import AddNewStrategy from './modals/addNewStrategy';
import { DEAL_PIPELINE_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY, DEAL_PIPELINE_INVESTMENTS_QUERY } from './queries';
import { StepsList } from './stepLists';
import Table from './table/table';

export const DealPipeline = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { searchParams, setSearchParams, onChangeSearch, onChangePrimaryFilter, makeScroll, saveScrollPostion, onChangePaginationData } =
    useSearchParamsManager();

  const q = searchParams.get('q') || '';
  const filterBarParams = searchParams.get('filterBar');
  const assetClassQuickFilter = searchParams.get('primaryFilter') || '';
  const key = (searchParams.get('key') as ListInvestmentSortBy) || ListInvestmentSortBy.LastUpdated;
  const asc = searchParams.get('asc') === 'true' || false;
  const position = searchParams.get('position') || 0;

  const [columnsList, setColumnsList] = useState<TableColumns[]>(dealPipelineTableColumns);
  const [tableRows, setTableRows] = useState<DealPipelineInvestmentsQuery['Investments']['data']>([]);
  const [updatesPopoverData, setUpdatesPopoverData] = useState<DealPipelineInvestmentsQuery['Investments']['data'][number] | null>(null);
  const [investmentWorkflowStateFilters, setInvestmentWorkflowStateFilters] = useState<
    DealPipelineInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters']
  >([]);
  const [investmentData, setInvestmentData] = useState<DealPipelineInvestmentsQuery['Investments'] | null>(null);
  const [filterBar, setFilterBar] = useState<IFilterBarData[]>([]);
  const [assetClassSelectOptions, setAssetClassSelectOptions] = useState<ISelectValue[]>([]);
  const [isTableColumnsEditorOpen, setIsTableColumnsEditorOpen] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [paginationData, setPaginationData] = useState({ page: 1, limit: initialValuesFromTable.initialAmountRowsPerPage[0] });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [total, setTotal] = useState(0);
  const [mobileSort, setMobileSort] = useState(dealPipelineMobileSort[0].title);
  const mobileSortList = dealPipelineMobileSort.map((item) => item.title);

  const variables = {
    sort: key,
    order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
    search: q.toLowerCase().trim(),
    rowsPerPage: paginationData.limit,
    page: 1,
    assetClassQuickFilter,
    statusFilter: filterBar.filter((item) => item.enabled).map((item) => item.value as string)
  };

  useEffect(() => {
    const newColumnsList = localStorage.getItem('dealPipelineColumnsOrder');
    if (newColumnsList) {
      const parsedColumnsList = JSON.parse(newColumnsList);

      const updatedColumns: TableColumns[] = parsedColumnsList
        .map((storedColumn: { [key: string]: string | boolean }) => {
          const matchingStoredColumn = dealPipelineTableColumns.find((column) => column.cellKey === storedColumn.cellKey);
          if (matchingStoredColumn) {
            return { ...matchingStoredColumn, isHidden: storedColumn.isHidden };
          }
        })
        .filter(Boolean);

      const existingCellKeys = new Set(updatedColumns.map((column) => column.cellKey).filter(Boolean));
      const missingColumns = dealPipelineTableColumns.filter((column) => column.cellKey && !existingCellKeys.has(column.cellKey));

      const allColumns = [...updatedColumns, ...missingColumns];

      const isFixedColumnExists = allColumns.some((column: TableColumns) => column.cellKey === 'name');

      if (isFixedColumnExists) {
        const [fixedColumns, otherColumns] = partition(allColumns, (column) => column.order === 0);
        setColumnsList([...fixedColumns, ...otherColumns]);
      } else {
        const fixedTableColumns = dealPipelineTableColumns.filter((column) => column.isFixed);
        const combinedColumns = [...fixedTableColumns, ...allColumns];
        const [fixedColumns, otherColumns] = partition(combinedColumns, (column) => column.order === 0);
        setColumnsList([...fixedColumns, ...otherColumns]);
      }
    }
  }, []);

  // Initialize filterBar when statuses are loaded
  useEffect(() => {
    if (!investmentWorkflowStateFilters.length) return;

    const initiallyDisabledFilters = [
      SECURITY_STATUS.REJECTED,
      SECURITY_STATUS.DECLINED,
      SECURITY_STATUS.ACTIVE,
      SECURITY_STATUS.REALIZED,
      SECURITY_STATUS.REDEEMED,
      SECURITY_STATUS.TERMINATED
    ];

    const initialFilterBar = investmentWorkflowStateFilters.map((item) => {
      let enabled = !initiallyDisabledFilters.includes(item.subjectStatus as SECURITY_STATUS);
      if (filterBarParams) {
        const parsedParams = JSON.parse(filterBarParams);
        enabled = parsedParams.includes(item.name);
      }

      const existingItem = filterBar.find((existing) => existing.value === item.name);

      return {
        name: item.label,
        type: 'statusFilter',
        value: item.name,
        enabled,
        count: existingItem?.count || 0
      };
    });

    setFilterBar(initialFilterBar);
  }, [investmentWorkflowStateFilters, filterBarParams]);

  // Update counts only after query completes
  useEffect(() => {
    if (!investmentData || !filterBar.length) return;

    const { filters } = investmentData;

    const updatedFilterBar = filterBar.map((item) => {
      const matchedFilter = filters.find((filter) => filter.value === item.value);
      return {
        ...item,
        count: matchedFilter?.count ?? item.count
      };
    });

    const countsChanged = updatedFilterBar.some((item, index) => item.count !== filterBar[index].count);

    if (countsChanged) {
      setFilterBar(updatedFilterBar);
    }
  }, [investmentData]);

  const handleInfiniteScroll = debounce(async () => {
    if (investmentsLoading || isTableColumnsEditorOpen || isLoadingMore || !hasMoreData || tableRows?.length === total) return;

    const isNearBottom = Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight - 200;

    if (!isNearBottom) return;

    try {
      setIsLoadingMore(true);

      const nextPage = paginationData.page + 1;

      const { data } = await investmentsRefetch({ ...variables, page: nextPage });

      const newRows = data.Investments.data;

      if (!newRows || newRows.length === 0) {
        setHasMoreData(false);
        return;
      }

      setPaginationData((prev) => ({ ...prev, page: nextPage }));
      setTableRows((prevTableRows) => {
        const existingIds = new Set(prevTableRows.map((row) => row.id));
        const uniqueNewRows = newRows.filter((row) => !existingIds.has(row.id));

        return [...prevTableRows, ...uniqueNewRows];
      });

      if (newRows.length < paginationData.limit) {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  }, 100);

  const resetInfiniteScroll = () => {
    setHasMoreData(true);
    setIsLoadingMore(false);
    setPaginationData((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    resetInfiniteScroll();
  }, [q, key, asc, filterBarParams, assetClassQuickFilter, isMobile]);

  useEffect(() => {
    window.addEventListener('scroll', handleInfiniteScroll);

    return () => {
      window.removeEventListener('scroll', handleInfiniteScroll);
    };
  }, [handleInfiniteScroll]);

  const {
    data: investmentsData,
    refetch: investmentsRefetch,
    loading: investmentsLoading
  } = useQuery(DEAL_PIPELINE_INVESTMENTS_QUERY, {
    errorPolicy: 'all',
    skip: !investmentWorkflowStateFilters.length || !filterBar.length,
    variables: {
      ...variables,
      page: 1
    },
    onCompleted: (data) => {
      const { data: tableData, total } = data.Investments;
      if (!isLoadingMore) {
        setTableRows(tableData);
        setInvestmentData(data.Investments);
      }

      setTotal(total);
      makeScroll(position);
    },
    onError: (error) => {
      console.error(error);
      if (investmentsData) {
        const { data: tableData, total } = investmentsData.Investments;
        if (!isLoadingMore) {
          setTableRows(tableData);
          setInvestmentData(investmentsData.Investments);
        }

        setTotal(total);
        makeScroll(position);
      }
    }
  });

  useQuery<{
    InvestmentWorkflowStateFilters: DealPipelineInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters'];
  }>(DEAL_PIPELINE_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY, {
    onCompleted: ({ InvestmentWorkflowStateFilters }) => {
      setInvestmentWorkflowStateFilters(InvestmentWorkflowStateFilters);
    }
  });

  const { loading: assetClassesLoading } = useQuery(GET_ASSET_CLASSES_QUERY, {
    onCompleted: ({ assetClasses }) => {
      const selectOptions = assetClasses.flatMap((assetClass) => {
        if (assetClass.subAssetClasses.length > 0) {
          return assetClass.subAssetClasses.map((subAssetClass) =>
            createSimpleSelectOption(subAssetClass.id, `${assetClass.name} - ${subAssetClass.name}`)
          );
        }
        return createSimpleSelectOption(assetClass.id, assetClass.name);
      });
      setAssetClassSelectOptions(selectOptions);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const handleInvestmentsQueryRefetch = () => {
    resetInfiniteScroll();
    investmentsRefetch({ ...variables, page: 1 });
  };

  const openPage = (path: string, options?: NavigateOptions) => {
    saveScrollPostion();
    navigate(path, options);
  };

  const handleFilterBar = (filterBarItems: IFilterBarData[]) => {
    const filterBarValues = filterBarItems.filter((item) => item.enabled).map((item) => item.value);
    setSearchParams(
      (prev) => {
        prev.set('filterBar', JSON.stringify(filterBarValues));
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
    setFilterBar(filterBarItems);
  };

  const handleMobileSort = (value: string) => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setMobileSort(value);
    const sortItem = dealPipelineMobileSort.find((item) => item.title === value);
    investmentsRefetch({
      sort: sortItem?.key as ListInvestmentSortBy,
      order: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      page: 1
    });
  };

  const handleTableColumnsEditorModalPage = ({ isOpen }: { isOpen: boolean }) => {
    setIsTableColumnsEditorOpen(isOpen);
    if (isOpen) {
      saveScrollPostion();
    } else {
      makeScroll(position);
    }
  };

  const openMobileModal = (data: DealPipelineInvestmentsQuery['Investments']['data'][number], modalType: string) => {
    if (modalType === 'updates') {
      setUpdatesPopoverData(data);
    }
  };

  const tableLoading = investmentsLoading;

  if (assetClassesLoading) return <LoaderOnWholeScreen />;

  if (isTableColumnsEditorOpen) {
    return (
      <TableColumnsEditor
        allColumns={dealPipelineTableColumns}
        columnsList={columnsList}
        tableName="dealPipeline"
        setColumnsList={setColumnsList}
        onClose={() => handleTableColumnsEditorModalPage({ isOpen: false })}
      />
    );
  }

  return (
    <>
      {updatesPopoverData && (
        <MobileModal
          title={'Updates'}
          onClose={() => {
            setUpdatesPopoverData(null);
          }}
          onClick={() => null}
        >
          <StepsList row={updatesPopoverData} initialDate={updatesPopoverData.updatedAt} isMobile />
        </MobileModal>
      )}
      {modalWindow.isOpen && modalWindow.type === 'add-new-strategy' && (
        <AddNewStrategy isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ isOpen: false, type: '' })} />
      )}
      <MainWrap>
        <Header />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <ButtonsWrapper>
            <Button size="md" onClick={() => setModalWindow({ isOpen: true, type: 'add-new-strategy' })} icon={<AddButtonIcon />}>
              Add New Strategy
            </Button>
            <Button
              size="md"
              styleType="outline"
              onClick={() => navigate('managerSpotlightWizard?backToTitle=Manager Pipeline')}
              icon={<EnvelopeIcon width={23} height={18} fill={themeVar('action.default')} />}
            >
              Manager Spotlight
            </Button>
          </ButtonsWrapper>
          <FilterPage
            search={{
              value: q,
              onChange: onChangeSearch,
              placeholder: 'Search'
            }}
            quickFilter={{
              title: 'Asset Class',
              value: assetClassQuickFilter,
              defaultValue: 'All',
              onChange: onChangePrimaryFilter,
              options: [{ label: 'All', value: '' }, ...assetClassSelectOptions]
            }}
            refetch={handleInvestmentsQueryRefetch}
            resultsValue={investmentsData?.Investments.total ?? 0}
            filterBar={filterBar}
            onChangeFilterBar={handleFilterBar}
            handleTableColumns={() => handleTableColumnsEditorModalPage({ isOpen: true })}
            mobileSort={{
              value: mobileSort,
              selects: mobileSortList,
              onChange: handleMobileSort
            }}
            useNewFilters
          />
          <Table
            columnsList={columnsList}
            tableRows={tableRows}
            tableLoading={tableLoading}
            workflowStates={investmentWorkflowStateFilters}
            forceRecalculateTableFiltersHeight={filterBar?.length}
            openPage={openPage}
            openMobileModal={openMobileModal}
          />
          {isLoadingMore && !tableLoading && <Loader />}
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;
